/* eslint-disable @typescript-eslint/no-explicit-any */
import { RouteProps, match } from 'react-router-dom';
import { ApolloClient } from '@apollo/client';
import { SvgIconProps } from '@material-ui/core/SvgIcon/SvgIcon';
import { MenuItemProps } from '@material-ui/core/MenuItem';
import { SweetAlertOptions, SweetAlertResult } from 'sweetalert2';
import {
  ThemeOptions as DefaultThemeOptions,
  Theme as DefaultTheme
} from '@material-ui/core/styles';
import { AlertVariant } from './enums';
import { PrivilegedContext } from 'store/user';
import { ProductCacheState } from 'store/cache/product-cache';
import { Product, ProductCurriculum, Student } from './schema-generated-types';

export interface AppBarProps {
  title: string;
  tabIndex?: number;
  children?: React.ReactNode;
}

export type AlertOptions = SweetAlertOptions & {
  variant: AlertVariant;
  onCancel?: () => void;
  onSubmit?: (result: SweetAlertResult) => void;
  deleteText?: string;
  text?: any; // TODO: type
  textItems?: any; // TODO: type
};

export type OpenAlertOptions = Omit<AlertOptions, 'variant'>;

export type OpenDeleteAlertOptions = OpenAlertOptions & { inputLabel: string };

export type SweetAlertHookApi = {
  open: (options?: OpenAlertOptions) => Promise<SweetAlertResult | void>;
};

export type DeleteSweetAlertHookApi = {
  open: (options?: OpenDeleteAlertOptions) => Promise<SweetAlertResult | void>;
};

export interface ThemeOptions extends DefaultThemeOptions {
  constants: {
    modals: {
      footerHeight: number;
      stepperHeight: number;
    };
  };
}

export interface Theme extends DefaultTheme {
  constants: {
    modals: {
      footerHeight: number;
      stepperHeight: number;
    };
  };
}

export type RouteTextOverrideArgs = {
  match: match<any>;
  client: ApolloClient<any>;
  productCache?: ProductCacheState;
} & RouteConfig;

export type GetBreadcrumbsResponse = {
  name: string;
  path: string;
  Icon?: React.ComponentType<SvgIconProps>;
}[];
export interface RouteConfig {
  Component?: any;
  AppBar?: any;
  NavBar?: any;
  id: RouteId;
  privileges: string[];
  childrenOverride?: React.ReactNode;
  MenuIcon?: any;
  menuGroup?: number;
  menuSortIndex?: number;
  path: string;
  getTitle: (args?: RouteTextOverrideArgs) => string;
  getBreadcrumbs?: (args: RouteTextOverrideArgs) => GetBreadcrumbsResponse;
}
export type ModuleProps = RouteProps & RouteConfig;
export interface ModuleTemplate {
  Component: React.FC<ModuleProps>;
  AppBar?: React.FC<RouteConfig>;
  NavBar?: React.FC<RouteConfig>;
}
export type ModuleConfig = RouteConfig & ModuleTemplate;

export type LayoutContainerProps = ModuleProps & Pick<ModuleTemplate, 'AppBar' | 'NavBar'>;

export type VoidCallback = () => void;

export enum RouteId {
  ADMIN_CLASSES_STUDENTS = 'AdminClassesStudents',
  ADMIN_CONSOLE = 'AdminConsole',
  ADMIN_HELP = 'AdminHelp',
  ADMIN_ORGANIZATIONS = 'AdminAdminOrganizations',
  ADMIN_PRODUCT_LICENSES = 'AdminProductLicenses',
  ADMIN_PRODUCT_LICENSES_ORDERS = 'Orders',
  ADMIN_ROSTER_MANAGEMENT = 'RosterManagement',
  ADMIN_USER_MANAGEMENT = 'AdminUserManagement',
  APP_ACCESS = 'AppAccess',
  APP_ACCESS_APP_DETAILS = 'AppAccessAppDetails',
  ASSESSMENT_ACCELERATOR_PREVIEW = ' AssessmentAcceleratorPreview',
  CLASSROOMS = 'Classrooms',
  CLASSROOMS_DETAILS = 'ClassroomDetails',
  DASHBOARD = 'Dashboard',
  DASHBOARD_ORDERS = 'DashboardOrders',
  LESSON_GRADE_PREVIEW = 'LessonGradePreview',
  LESSON_WEEK_PREVIEW = 'LessonWeekPreview',
  LOGOUT = 'Logout',
  MESSAGES = 'Messages',
  ORDER = 'Order',
  ORDERS = 'Orders',
  PRINT_SCREEN = 'PrintScreen',
  REPORTS = 'Reports',
  RESOURCES = 'TeachingResources',
  STUDENTS = 'Students',
  SUPPORT = 'Support',
  SUPPORT_CATEGORY = 'SupportCategory',
  UNAUTHENTICATED_ORDERS = 'UnauthenticatedOrders',
  USER_PROFILE = 'UserProfile',
  WELCOME_PAGE = 'Welcome'
}

export type ActionOption = {
  MenuItemProps?: MenuItemProps & { component?: React.ElementType } & {
    button?: true | undefined;
  };
  label: string;
  isPrivileged?: (data: PrivilegedContext) => boolean;
};
export interface ModalCloseOptions {
  dirty?: boolean;
}

export enum ClassroomStatus {
  ACTIVE = 'ACTIVE',
  ARCHIVED = 'ARCHIVED'
}

export type StudentRowDataProps = Pick<
  Student,
  | 'firstName'
  | 'lastName'
  | 'id'
  | 'username'
  | 'gradeLevel'
  | 'password'
  | 'email'
  | 'licenseUsage'
> & { color: string; ExpansionComponent?: React.ReactNode };

export interface ProductWithCurriculum {
  product: Product;
  productCurriculum?: ProductCurriculum;
}
